import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { CheckCircle, ArrowClockwise, X, PencilSimpleLine } from "@phosphor-icons/react";

/**
 * MobileSign — a touch-friendly signature pad delivered via QR-code session.
 * Captures strokes on a canvas, converts to PNG dataURL, and POSTs it to the
 * /api/photo-sync/{token}/upload endpoint. The desktop receives it live via WS.
 */
export default function MobileSign() {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasDrawnRef = useRef(false);
  const [sessionOk, setSessionOk] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | drawing | uploading | sent
  const [error, setError] = useState("");

  // Validate session
  useEffect(() => {
    api.get(`/photo-sync/${token}/status`)
      .then((r) => {
        const expires = new Date(r.data.expires_at);
        if (expires < new Date()) {
          setSessionOk(false);
          setError("This session has expired. Restart from the desktop.");
        } else {
          setSessionOk(true);
        }
      })
      .catch(() => {
        setSessionOk(false);
        setError("Invalid or expired session.");
      });
  }, [token]);

  // Initialise the canvas with white background
  useEffect(() => {
    if (!sessionOk) return;
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
  }, [sessionOk]);

  const getPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const onDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    hasDrawnRef.current = true;
    lastPointRef.current = getPoint(e);
    setStatus("drawing");
  };

  const onMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };

  const onUp = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clearPad = () => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasDrawnRef.current = false;
    setStatus("idle");
  };

  const submit = async () => {
    if (!hasDrawnRef.current) {
      toast.error("Please draw your signature first");
      return;
    }
    setStatus("uploading");
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await api.post(`/photo-sync/${token}/upload`, { photo_base64: dataUrl });
      setStatus("sent");
      toast.success("Signature sent");
    } catch (e) {
      setStatus("drawing");
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    }
  };

  if (sessionOk === false) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <X size={36} weight="duotone" className="text-[var(--sms-danger)] mx-auto mb-4" />
          <div className="font-display text-2xl font-black tracking-tighter mb-2">Session expired</div>
          <div className="text-sm text-slate-500">{error}</div>
        </div>
      </div>
    );
  }
  if (sessionOk === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-slate-400">Loading session…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="font-display text-lg font-black tracking-tighter">SMS<span className="text-[var(--sms-primary)]">.</span></div>
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Mobile signature pad</div>
      </header>

      <main className="flex-1 px-5 py-6 max-w-md mx-auto w-full">
        <h1 className="font-display text-3xl font-black tracking-tighter mb-2">
          {status === "sent" ? "Signed." : "Sign here."}
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          {status === "sent"
            ? "Your signature has been delivered to the desktop. You can close this tab."
            : "Use your finger to draw your signature inside the box, then tap Send."}
        </p>

        <div className="bg-white border-2 border-slate-900 mb-3" style={{ height: 240 }}>
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
            className="w-full h-full touch-none"
          />
        </div>

        <div className="flex items-center gap-2 mb-5 text-[10px] font-mono-sms uppercase tracking-[0.2em] text-slate-400">
          <PencilSimpleLine size={12} weight="bold" /> Draw above
        </div>

        <div className="space-y-3">
          {status !== "sent" && (
            <>
              <button onClick={submit} data-testid="mobile-sign-send" className="sms-btn-primary w-full">
                {status === "uploading" ? "Sending…" : "Send signature"}
              </button>
              <button onClick={clearPad} data-testid="mobile-sign-clear" className="sms-btn-secondary w-full inline-flex items-center justify-center gap-2">
                <ArrowClockwise size={14} weight="bold" /> Clear
              </button>
            </>
          )}
          {status === "sent" && (
            <div className="text-center pt-4">
              <CheckCircle size={48} weight="duotone" className="text-[var(--sms-success)] mx-auto mb-2" />
              <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-success)]">Delivered</div>
            </div>
          )}
        </div>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
